import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { MeterConsumptionGranularityTypes, MeterDownloadWizardSelection } from '@app/shared/enums';
import { AuthService } from '@app/shared/services/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { cssVariables } from '@env/css-vars';
import { MeterExportService } from '@app/shared/services/api/meter-export.service';
import * as moment from 'moment';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { isNil, remove } from 'lodash';
import { MeterView } from '@app/shared/models/meter-data/meter-view';
import { EnergyDataFilter, EnergyDataStoreService } from '@app/shared/services/store/energy-data-store.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ISODateTimeStartOfDayFormat } from '@app/shared/constants';
import { StringUtility } from '@app/shared/string-utility';
import {
  MeterConsumptionGranularityKeys,
  MeterDownloadWizardDateRange,
  MeterDownloadWizardDto,
  MeterDownloadWizardFilter,
} from '@app/shared/models/meter/meter-download-wizard-data';
import { CheckedMeter } from '@app/shared/models/meter/meter-wizard-checked-meter';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-meter-download-wizard',
  templateUrl: './meter-download-wizard.component.html',
  styleUrls: ['./meter-download-wizard.component.scss'],
})
export class MeterDownloadWizardComponent implements OnInit, OnDestroy {
  @Input() currentStep = 1;
  @Input() isFromEnergyData = false;
  @Input() visibility = false;
  @Input() meter: MeterView = {} as MeterView;
  @Input() isSmd = false;
  @Input() obisId = '';
  @Output() closeEvent = new EventEmitter<boolean>();

  numberOfSteps = 4;
  isValid = true;
  isDisabled = true;
  title = '';
  filter: MeterDownloadWizardFilter = {
    userInfo: {
      email: this.authService.currentUser.email,
      firstname: this.authService.currentUser.firstname,
      lastname: this.authService.currentUser.lastname,
    },
    meterSelection: null,
    granularity: null,
    granularityText: null,
    meterData: [],
    dateRange: {} as MeterDownloadWizardDateRange,
    fileExtension: null,
    tenant: this.authService.currentUser.tenant,
    municipalityKey: this.userMunicipalityService.currentMunicipality.key,
    timeStamp: null,
  };

  oldGranularity: string | null = null;
  filterInitialValues: EnergyDataFilter = {} as EnergyDataFilter;

  constructor(
    public authService: AuthService,
    public meterExportService: MeterExportService,
    public matDialog: MatDialog,
    private overlayLoader: OverlayLoaderService,
    private energyDataStore: EnergyDataStoreService,
    private userMunicipalityService: UserMunicipalitiesService
  ) {}

  ngOnInit() {
    this.filterInitialValues = this.energyDataStore.filter;
  }

  ngOnDestroy() {
    this.energyDataStore.filter = this.filterInitialValues;
  }

  public next(): void {
    if (this.currentStep === this.numberOfSteps) {
      if (this.isMeterSelectionIdentical(this.filter.meterSelection, MeterDownloadWizardSelection.current)) {
        const data: MeterDownloadWizardDto = {
          id: this.meter.guid,
          meterNumber: this.meter.meterNumber,
          marketLocationKey: this.meter.marketLocationKey,
          address: this.meter.getAddress().replace('\r\n', ' '),
          meterTypesName: this.meter.meterTypesName,
          meterTypeCode: this.meter.meterTypeCode,
          energyTypesName: this.meter.energyTypesName,
          converterFactor: this.meter.converterFactor,
          meterOperator: this.meter.getMarketOperatorText(),
          marketOperatorCategory: this.meter.getMarketOperatorCategory(),
          deviceId: this.meter.deviceId,
        };
        const object = this.filter.meterData.find((x) => x.id === data.id);

        if (isNil(object)) {
          this.filter.meterData.push(data);
        }
      }

      for (const el in this.filter.dateRange) {
        if (this.filter.dateRange[el as keyof MeterDownloadWizardDateRange] === null) {
          delete this.filter.dateRange[el as keyof MeterDownloadWizardDateRange];
        }
      }

      this.filter.timeStamp = moment().format('YYYY-MM-DD-HH-mm');
      const filter = this.filter;
      const newDateRange = this.standardizeDateRangeFormat(this.filter.dateRange, this.filter.granularity as MeterConsumptionGranularityKeys);
      filter.dateRange = newDateRange;

      const meterExportJobDTO = {
        message: JSON.stringify(filter),
      };
      this.overlayLoader.isLoading = true;
      this.meterExportService
        .post(meterExportJobDTO)
        .pipe(
          catchError((error: HttpErrorResponse) => {
            this.downloadError();
            return throwError(() => error);
          })
        )
        .subscribe(() => {
          this.overlayLoader.isLoading = true;
          this.complete();
        });
    } else if (this.currentStep >= 0) {
      switch (this.currentStep) {
        case 0:
          this.isValid = true;
          break;

        case 1:
          if (this.isMeterSelectionIdentical(this.filter.meterSelection, MeterDownloadWizardSelection.current)) {
            this.currentStep++;
          } else {
            this.filter.meterSelection = MeterDownloadWizardSelection.multiple;
          }

          if (
            MeterConsumptionGranularityTypes[this.filter.granularity as MeterConsumptionGranularityKeys] === MeterConsumptionGranularityTypes.yearly &&
            this.isMeterSelectionIdentical(this.filter.meterSelection, MeterDownloadWizardSelection.current)
          ) {
            this.currentStep--;
          }

          if (this.oldGranularity !== this.filter.granularity) {
            this.filter.dateRange = {} as MeterDownloadWizardDateRange;
          }

          this.isValid = true;
          break;

        case 2:
          this.isValid = true;
          break;

        case 3:
          this.isValid = true;
          break;
      }

      if (this.isValid) {
        this.currentStep++;
      }
    }
  }

  public previous() {
    switch (this.currentStep) {
      case 0:
        this.visibility = false;
        this.closeEvent.emit(false);
        this.currentStep--;
        break;

      case 1:
        if (this.isFromEnergyData) {
          this.visibility = false;
          this.closeEvent.emit(false);
        }
        this.currentStep--;
        break;

      case 2:
        this.currentStep--;
        break;

      case 3:
        this.oldGranularity = this.filter.granularity;
        if (this.isMeterSelectionIdentical(this.filter.meterSelection, MeterDownloadWizardSelection.current)) {
          this.currentStep--;
        }
        this.currentStep--;
        break;

      case 4:
        this.currentStep--;
        break;
    }
  }

  closeWizard() {
    this.visibility = false;
    this.closeEvent.emit(false);
  }

  getTitle(value: string) {
    this.title = value;
  }

  getNextButtonState(status: boolean) {
    this.isDisabled = status;
  }

  storeSelectedGranularity(value: MeterConsumptionGranularityKeys) {
    this.filter.granularity = value;
    this.filter.granularityText = MeterConsumptionGranularityTypes[value];
    this.filter.meterData = [];
  }

  storeFileExtension(value: string) {
    this.filter.fileExtension = value;
  }

  storeMeterSelection(value: string) {
    this.filter.meterSelection = value;
  }

  onCheckMeter(event: CheckedMeter) {
    const object = this.filter.meterData.find((x) => x.id === event.id);

    if (event.checked) {
      if (isNil(object)) {
        this.filter.meterData.push({
          id: event.id,
          meterNumber: event.meterNumber,
          marketLocationKey: event.marketLocationKey,
          address: event.address,
          meterTypesName: event.meterTypesName,
          meterTypeCode: event.meterTypeCode,
          energyTypesName: event.energyTypesName,
          converterFactor: event.converterFactor,
          meterOperator: event.meterOperator,
          marketOperatorCategory: event.marketOperatorCategory,
          deviceId: event.deviceId,
        });
      }
    } else {
      remove(this.filter.meterData, object);
    }
  }

  complete() {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Sie erhalten eine E-Mail mit Ihren angefragten Verbrauchswerten sobald der Datenexport abgeschlossen ist.',
          descriptionFaClass: 'fa-check-circle fa-2x',
          descriptionFaColor: cssVariables['--confirm-success-color'],
          hideClose: true,
        },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe(() => {
        this.closeWizard();
      });
  }

  downloadError = () =>
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Download fehlgeschlagen. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
          descriptionFaClass: 'fa-exclamation-triangle fa-2x',
          descriptionFaColor: cssVariables['--ep-error-color'],
        },
        position: { top: '40px' },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe(() => {
        this.closeWizard();
      });

  standardizeDateRangeFormat(dateRange: MeterDownloadWizardDateRange, granularity: MeterConsumptionGranularityKeys): MeterDownloadWizardDateRange {
    const minDate = moment(new Date()).subtract(5, 'years');
    const maxDate = moment(new Date());

    let startDate = dateRange.startDate;
    let endDate = dateRange.endDate;
    const startYearInString = dateRange.startYear as string;
    const startYearInNumber = parseInt(startYearInString);
    const endYearInString = dateRange.endYear as string;
    const endYearInNumber = parseInt(endYearInString);

    if (StringUtility.areStringsIdentical(MeterConsumptionGranularityTypes[granularity], MeterConsumptionGranularityTypes.daily)) {
      const startMonth = moment()
        .month(dateRange.startMonth as string)
        .subtract(1, 'months')
        .format('M');
      const endMonth = moment()
        .month(dateRange.endMonth as string)
        .subtract(1, 'months')
        .format('M');

      startDate = moment(new Date(startYearInNumber, parseInt(startMonth, 10) % 12, 1)).format(ISODateTimeStartOfDayFormat);
      endDate = moment(new Date(endYearInNumber, parseInt(endMonth, 10) % 12, 1))
        .endOf('month')
        .format(ISODateTimeStartOfDayFormat);
    }

    if (StringUtility.areStringsIdentical(MeterConsumptionGranularityTypes[granularity], MeterConsumptionGranularityTypes.weekly)) {
      const startWeek = parseInt((dateRange.startWeek as string).split(' ')[1], 10);
      const endWeek = parseInt((dateRange.endWeek as string).split(' ')[1], 10);
      const firstDayOfYear = moment().year(startYearInNumber).startOf('year');

      startDate = firstDayOfYear
        .add(startWeek - 1, 'weeks')
        .day(1)
        .format(ISODateTimeStartOfDayFormat);
      endDate = moment().isoWeekYear(endYearInNumber).isoWeek(endWeek).endOf('week').format(ISODateTimeStartOfDayFormat);
    }

    if (
      StringUtility.areStringsIdentical(MeterConsumptionGranularityTypes[granularity], MeterConsumptionGranularityTypes.monthly) ||
      StringUtility.areStringsIdentical(MeterConsumptionGranularityTypes[granularity], MeterConsumptionGranularityTypes.yearly)
    ) {
      const startDay = moment().format('DD');
      const startMonth = moment().subtract(1, 'months').format('M');

      startDate =
        parseInt(startYearInString, 10) === minDate.year()
          ? moment(new Date(startYearInNumber, parseInt(startMonth, 10), parseInt(startDay, 10))).format(ISODateTimeStartOfDayFormat)
          : moment(new Date(startYearInNumber, 0, 1)).format(ISODateTimeStartOfDayFormat);

      endDate =
        parseInt(endYearInString, 10) === maxDate.year()
          ? moment(new Date()).format(ISODateTimeStartOfDayFormat)
          : moment(new Date(endYearInNumber, 0, 1)).endOf('month').format(ISODateTimeStartOfDayFormat);
    }

    if (StringUtility.areStringsIdentical(MeterConsumptionGranularityTypes[granularity], MeterConsumptionGranularityTypes.periodic)) {
      startDate = moment(new Date(startYearInNumber, 0, 1)).format(ISODateTimeStartOfDayFormat);

      endDate =
        parseInt(endYearInString, 10) === maxDate.year()
          ? moment(new Date()).format(ISODateTimeStartOfDayFormat)
          : moment(new Date(endYearInNumber, 0, 1)).endOf('month').format(ISODateTimeStartOfDayFormat);
    }

    return {
      startDate: startDate,
      endDate: endDate,
    } as MeterDownloadWizardDateRange;
  }

  private isMeterSelectionIdentical(selected: string | null, expected: string): boolean {
    return selected === expected;
  }
}
